// ─── Contentful Raw Types ────────────────────────────────────────────
import { MediaAssetData } from '@/components/Media/types';
import type { Document } from '@contentful/rich-text-types';

export interface ContentfulSys {
  id: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  locale?: string;
  revision?: number;
  space?: {
    sys: {
      type: 'Link';
      linkType: 'Space';
      id: string;
    };
  };
  environment?: {
    sys: {
      type: 'Link';
      linkType: 'Environment';
      id: string;
    };
  };
  contentType: {
    sys: {
      type: 'Link';
      linkType: 'ContentType';
      id: string;
    };
  };
}

export interface ContentfulEntry<T = Record<string, unknown>> {
  sys: ContentfulSys;
  fields: T;
  metadata?: {
    tags: Array<{
      sys: {
        type: 'Link';
        linkType: 'Tag';
        id: string;
      };
    }>;
  };
}

export interface ContentfulAsset {
  sys: Omit<ContentfulSys, 'contentType'> & {
    type: 'Asset';
  };
  fields: {
    title: string;
    description?: string;
    file: {
      url: string;
      details: {
        size: number;
        image?: {
          width: number;
          height: number;
        };
      };
      fileName: string;
      contentType: string;
    };
  };
  metadata?: ContentfulEntry['metadata'];
}

export interface ContentfulSiteReference {
  sys: {
    id: string;
  };
}

export type EntryWithBrandList = {
  fields?: object | null;
};

// ─── Type Guards ─────────────────────────────────────────────────────

export function isContentfulEntry(value: unknown): value is ContentfulEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    'sys' in value &&
    (value as { sys: unknown }).sys !== null &&
    typeof (value as { sys: { type?: unknown } }).sys.type === 'string' &&
    (value as { sys: { type: string } }).sys.type === 'Entry'
  );
}

export function isContentfulAsset(value: unknown): value is ContentfulAsset {
  if (typeof value !== 'object' || value === null) return false;
  if (!('fields' in value)) return false;
  const fields = (value as { fields: unknown }).fields;
  if (typeof fields !== 'object' || fields === null) return false;
  if (!('file' in fields)) return false;
  const file = (fields as { file: unknown }).file;
  if (typeof file !== 'object' || file === null) return false;
  return 'url' in file;
}

export interface ContentfulLinkFields {
  /** Internal identifier shown in Contentful entry list (Display Name / Entry title). */
  displayName: string;
  title: string;
  label: string;
  target?: string;
  icon?: ContentfulAsset;
  relAttribute?: string;
  url?: string;
  priority?: 'Primary' | 'Secondary' | 'CallRail' | 'Default';
}

// ─── Navigation Grid Content Type ─────────────────────

export interface ContentfulNavigationGridFields {
  anchorId?: string;
  name: string;
  items?: ContentfulEntry<ContentfulLinkFields>[];
}

export type ContentfulNavigationGridEntry = ContentfulEntry<ContentfulNavigationGridFields>;

export interface ContentfulMenuFields {
  title: string;
  menuName?: string;
  menuItems?: Array<
    ContentfulEntry<ContentfulMenuFields> | ContentfulEntry<ContentfulLinkFields> | null
  >;
}

export interface NavigationLink {
  __typename: 'Link';
  id: string;
  label: string; //title
  title?: string;
  url: string;
  targetAttribute: string;
  icon?: MediaAssetData;
  relAttribute: string;
  priority?: 'Primary' | 'Secondary' | 'CallRail' | 'Default';
  resolvedPhoneNumber?: string;
  resolvedTelHref?: string;
}

export interface NavigationMenu {
  __typename: 'NavigationMenu';
  id: string;
  title: string;
  menuName: string | null; //toggleLabel
  items: NavigationItem[];
  primaryCtaLink?: NavigationLink | null;
  callRailCtaLink?: NavigationLink | null;
}

export type NavigationItem = NavigationLink | NavigationMenu;

// ─── Text Pair Content Type ─────────────────────

export interface ContentfulTextPairsFields {
  /** Internal identifier shown in Contentful entry list (Display Name / Entry title). */
  displayName: string;
  title: string;
  copy?: Document;
  brandList?: string[];
}

export interface ContentfulBrandIntegrations {
  title: string;
  brandCode: string;
  gtmId: string;
  callRailId: string;
  callRailPhoneNumber: string;
  googleMapsApi: string;
  googleSiteVerification: string;
  msClarity: string;
  schedulerApiKey: string;
  schedulerId: string;
  trustIndexId01Long: string;
  trustIndexId02Short: string;
  serviceTitanApplicationKey: string;
  serviceTitanTenantId: string;
  serviceTitanClientId: string;
  serviceTitanClientSecret: string;
  serviceTitanExcludeIds?: string[];
  serviceTitanExcludeZonesForServiceType?: Array<{ zoneId: string; serviceTypeId: string }>;
  vwoAccountId?: string;
  cookieYesId?: string;
  cookieYesOptOut?: boolean;
  textFormId?: string;
}

export interface ContentfulBrandSettingsFields {
  title: string;
  brandNameShort: string;
  brandNameLong?: string;
  brandCode: string;
  logo?: ContentfulAsset;
  mainNavigation?: ContentfulEntry<ContentfulMenuFields>;
  footerNavigation?: ContentfulEntry<ContentfulMenuFields>;
  articlesPerPage?: number;
  defaultOpenGraphImage?: ContentfulAsset;
  defaultBrandImage?: ContentfulAsset;
  excludedLocationStates?: string[];
}

export type ContentfulTextPairsEntry = ContentfulEntry<ContentfulTextPairsFields>;

export type ContentfulSEOFields = ContentfulEntry<{
  metaTitle?: string;
  metaDescription?: string;
  canonicalOverride?: string;
  ogTitle?: string;
  ogDescription?: string;
  noFollow: boolean;
  noIndex: boolean;
}>;
// ─── Location Page Content Type ─────────────────────

export interface LocationPageFields {
  name: string;
  slug?: string;
  schedule?: Document;
  localizedIntro?: string;
  endingCopy?: string;
}

export type LocationPageEntry = ContentfulEntry<LocationPageFields>;

export interface OfficeFields {
  title?: string;
  address?: string;
  city?: string;
  stateFull?: string;
  stateShort?: string;
  zipCode?: string;
  brandList?: ContentfulEntry[];
}

export type OfficeEntry = ContentfulEntry<OfficeFields>;

// ─── Redirect Content Type ───────────────────────────────────────────

export type RedirectType = '301' | '302';

export interface ContentfulRedirectFields {
  title: string;
  origin: string;
  destination: string;
  redirectType: RedirectType;
  brandList?: ContentfulEntry[]; // empty = global (applies to all brands)
}

export type ContentfulRedirectEntry = ContentfulEntry<ContentfulRedirectFields>;
